/**
 * Quantities, as strings, exactly.
 *
 * The API speaks quantity as a numeric(14,4) STRING — '10.0000' — for the same
 * reason money is cents: `0.1 + 0.2` is not `0.3`, and a count that drifts is a
 * count nobody trusts. A shelf holding 2.5kg of bolts three times over holds
 * 7.5kg, not 7.499999999999999, and the difference is a variance somebody has
 * to explain at six o'clock in a storeroom.
 *
 * SO THERE IS NO `parseFloat` IN THIS FILE, and none in the screens that use
 * it. Arithmetic here is BigInt over scaled integers — the same discipline
 * money gets — and the only thing that ever becomes a JS number is a string
 * length.
 *
 * `parseFloat` is not merely imprecise, it is WRONG ABOUT WHAT IS A QUANTITY:
 * it reads '12 cans' as 12, '1e3' as 1000 and '0x10' as 0, silently. Every one
 * of those is a person mis-keying a count and being told they succeeded.
 */

/** Four decimal places, because that is stock_layer.quantity's scale. */
const DP = 4;
const SCALE_DIGITS = '0'.repeat(DP);

/**
 * The shape, with the PRECISION left to an explicit length check below.
 *
 * numeric(14,4) is ten digits before the point and four after, and both halves
 * matter — an eleventh digit raises 22003, which the API maps nowhere and which
 * would reach the browser as a 500. It is checked separately rather than as
 * `\d{0,10}` here so that eleven digits gets a refusal saying so, instead of
 * falling through to the anonymous "not a quantity".
 */
const UNSIGNED = /^(\d*)(?:\.(\d{0,4}))?$/;
const SIGNED = /^(-?)(\d*)(?:\.(\d{0,4}))?$/;

export const ZERO = `0.${SCALE_DIGITS}`;

/** A refusal a form can put in front of the user unchanged. */
export class QuantityError extends Error {
  constructor(message) {
    super(message);
    this.name = 'QuantityError';
  }
}

/**
 * A user-typed quantity as the canonical 4dp string the API accepts.
 *
 * WHAT IS ACCEPTED AND WHY, because the line is a decision and not an accident:
 *
 *  - '10', '10.5', '2.5000'  — the ordinary cases.
 *  - '.5' becomes '0.5000' and '1.' becomes '1.0000'. The API's own regex
 *    refuses both as written, but a thumb on a phone keypad genuinely means
 *    a half and genuinely means one, there is no second reading of either,
 *    and what leaves this function is a string the API accepts. Canonicalising
 *    is the whole job; refusing here would only make the user type a zero.
 *  - '  6 ' is trimmed, for the same reason.
 *
 * WHAT IS REFUSED:
 *
 *  - '1.00005' and anything else past four decimals. REFUSED, NOT ROUNDED —
 *    the same call toCents makes for a half-cent. A silently rounded count is
 *    a discrepancy with no trace, and the person who typed it will never know
 *    which figure the books took.
 *  - '1e3'. parseFloat says 1000; a person counting a shelf did not mean 1000,
 *    they mis-keyed. Exponent notation is the clearest single tell that a float
 *    coercion has been let in somewhere, so it is refused by name.
 *  - '', 'abc', '1,5', '12 cans', '0x10', 'Infinity', 'NaN' — not quantities.
 *  - a sign, unless `signed` is set. A count is what is on the shelf and there
 *    is no such shelf; an ADJUSTMENT is signed, and asks for it explicitly.
 *  - more than ten digits before the point: numeric(14,4) raises 22003, which
 *    the API maps nowhere and which would reach the browser as a 500.
 *
 * '-0' with `signed` is '0.0000'. Negative zero is not a quantity, and a
 * variance of nothing must read the same however it was arrived at.
 */
export function parseQuantity(input, { signed = false } = {}) {
  if (input === null || input === undefined) {
    throw new QuantityError('enter a quantity');
  }
  const raw = String(input).trim();
  if (raw === '') throw new QuantityError('enter a quantity');

  if (/e/i.test(raw)) {
    throw new QuantityError(
      `"${raw}" is not a quantity — write the digits out in full, without an exponent`);
  }

  const m = (signed ? SIGNED : UNSIGNED).exec(raw);
  if (!m) {
    // The two refusals worth naming apart, because the fix differs.
    if (!signed && /^-/.test(raw)) {
      throw new QuantityError(
        'a counted quantity cannot be negative — a shelf holds nothing or it holds something');
    }
    if (/\.\d{5,}$/.test(raw)) {
      throw new QuantityError(
        `"${raw}" has more than ${DP} decimal places, and rounding it here would put a `
        + 'difference in the books that nobody could trace back');
    }
    throw new QuantityError(`"${raw}" is not a quantity`);
  }

  const [, a, b, c] = signed ? m : [null, '', m[1], m[2]];
  const sign = signed ? a : '';
  const whole = b ?? '';
  const frac = c ?? '';

  // '.', '-' and '' all match the regex with both halves empty. None is a
  // number, and letting '.' through as zero would turn a slipped thumb into a
  // counted line of nothing — the one thing this module refuses everywhere.
  if (whole === '' && frac === '') throw new QuantityError(`"${raw}" is not a quantity`);
  if (whole.length > 10) {
    throw new QuantityError(`"${raw}" is too large — quantities go up to 9 999 999 999`);
  }

  const canonical = `${sign}${whole === '' ? '0' : whole}`
    + `.${(frac + SCALE_DIGITS).slice(0, DP)}`;
  // -0.0000 is 0.0000.
  return canonical === `-0.${SCALE_DIGITS}` ? ZERO : canonical;
}

/** The signed form, for an adjustment: negative writes stock off, positive on. */
export const parseSignedQuantity = (input) => parseQuantity(input, { signed: true });

/** True when parseQuantity would accept it. For enabling a button, not for arithmetic. */
export function isQuantity(input, options) {
  try { parseQuantity(input, options); return true; } catch { return false; }
}

/** The refusal message, or null when it parses. For showing under a field as it is typed. */
export function quantityError(input, options) {
  try { parseQuantity(input, options); return null; } catch (e) { return e.message; }
}

/**
 * A quantity for display.
 *
 * Trailing zeros go, and the point with them when nothing is left: a shelf of
 * ten cans reads `10`, not `10.0000`, and 2.5kg of bolts reads `2.5`. Four
 * decimal places on every row is noise that makes the one row with a genuine
 * fraction harder to see, not easier.
 *
 * NULL AND UNDEFINED READ `—`, NEVER `0`, and that is the single most important
 * line in this file. A zero says "we counted and there were none"; a dash says
 * "this is not the kind of thing that has a quantity", or "nobody has counted
 * this yet". Those are different facts about the world and confusing them is
 * how stock software lies. `money()` in api.js draws exactly the same line.
 */
export function formatQuantity(value) {
  if (value === null || value === undefined || value === '') return '—';
  const s = String(value);
  const [w, f = ''] = s.split('.');
  const frac = f.replace(/0+$/, '');
  // '-0.0000' is nothing, and nothing has no sign. Only once the fraction is
  // gone, so '-0.5000' keeps its minus and reads '-0.5'.
  const whole = w === '' || w === '-' || (w === '-0' && frac === '') ? '0' : w;
  return frac === '' ? whole : `${whole}.${frac}`;
}

/** A quantity as a scaled BigInt: '2.5000' -> 25000n. Canonical or not. */
function scaled(value) {
  const canonical = parseSignedQuantity(value);
  const neg = canonical.startsWith('-');
  const [whole, frac] = (neg ? canonical.slice(1) : canonical).split('.');
  const n = BigInt(whole + frac);
  return neg ? -n : n;
}

/** A scaled BigInt back to a canonical 4dp string. */
function unscaled(n) {
  const neg = n < 0n;
  const digits = (neg ? -n : n).toString().padStart(DP + 1, '0');
  const out = `${digits.slice(0, -DP)}.${digits.slice(-DP)}`;
  return neg ? `-${out}` : out;
}

/** a + b, exactly. Both operands may be signed. */
export const addQuantity = (a, b) => unscaled(scaled(a) + scaled(b));

/** a - b, exactly. This is how a variance is computed: counted less expected. */
export const subtractQuantity = (a, b) => unscaled(scaled(a) - scaled(b));

/** True when the quantity is exactly nothing, however it was written. */
export const isZeroQuantity = (value) => scaled(value) === 0n;

/** True when the quantity is below nothing — a shortfall. */
export const isNegativeQuantity = (value) => scaled(value) < 0n;
